import { ElementRef, Injectable, signal } from '@angular/core';
import esriConfig from "@arcgis/core/config.js";
import { environment } from '@env/environment';
import WebMap from "@arcgis/core/WebMap";
import MapView from '@arcgis/core/views/MapView';
import Home from '@arcgis/core/widgets/Home';
import Track from '@arcgis/core/widgets/Track';
import Zoom from "@arcgis/core/widgets/Zoom";
import Graphic from '@arcgis/core/Graphic';
import PolyLine from '@arcgis/core/geometry/Polyline';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import Point from '@arcgis/core/geometry/Point';
import BasemapGallery from '@arcgis/core/widgets/BasemapGallery';
import { Coordinates } from '@api/models';
import { Itinerary, Leg } from '../../route-form/models/routeRequestResults.model';
import { decode } from '@googlemaps/polyline-codec';
import { UtilsService } from '@shared/services/utils.service';

@Injectable({
  providedIn: 'root'
})
export class MapService {

  public map!: WebMap;
  public view!: MapView;
  public mapLoaded = signal(false);

  public baseMapGallery!: BasemapGallery;
  public mapRotation = signal(0);
  private trackWidget!: Track;

  public showBaseMapGallery = signal(false);
  public showLayersList = signal(false);
  public showLegend = signal(false);

  public waitingForClick = signal(false);

  public showingResults = signal(false);
  public routeGraphicsDrawn: any[] = [];

  public contextMenu: ElementRef | undefined;

  public constructor(private utilsService: UtilsService) { }
  public setEsriConfig() {
    esriConfig.portalUrl = environment.webMapUrl;
  }

  public toggleShowBaseMapGallery() {
    if (!this.showBaseMapGallery()) {
      this.showLayersList.set(false);
      this.showLegend.set(false);
    }
    this.showBaseMapGallery.set(!this.showBaseMapGallery());
  }

  public toggleShowLayersList() {
    if (!this.showLayersList()) {
      this.showBaseMapGallery.set(false);
      this.showLegend.set(false);
    }
    this.showLayersList.set(!this.showLayersList());
  }

  public toggleShowLegend() {
    if (!this.showLegend()) {
      this.showLayersList.set(false);
      this.showBaseMapGallery.set(false);
    }
    this.showLegend.set(!this.showLegend());
  }

  public trackWidgetIsActive() {
    return this.trackWidget.tracking;
  }

  public getCurrentCoordinates(): Coordinates | null {
    const geometry = this.trackWidget.graphic.geometry as any;
    if (!geometry) {
      this.utilsService.showSnackBarError('canNotGetYourUbication');
      return null;
    }
    const coordinates: Coordinates = {
      lat: geometry.latitude,
      lng: geometry.longitude,
    }
    return coordinates;
  }

  public centerToCurrentLocation() {
    const geometry = this.trackWidget.graphic.geometry as any;
    this.view.goTo({
      target: geometry,
      zoom: 17
    });
  }

  public createMap() {
    this.map = new WebMap({
      portalItem: {
        id: environment.webMapId
      }
    });
    return this.map;
  }

  public setContextMenu(contextMenu: ElementRef) {
    this.contextMenu = contextMenu;
  }

  public createView(locateButton?: ElementRef, callback?: () => void) {
    this.view = new MapView({
      map: this.map,
      zoom: 13, // Zoom level
      container: "viewDiv" // Div element
    });

    this.addBaseWidgets(locateButton);
    this.view.when(() => {
    }).then(() => {
      this.listenRotation();
      this.trackWidget.start();
      this.view.container.appendChild(this.contextMenu?.nativeElement);
      this.mapLoaded.set(true)
    }).then(() => {
      this.view.on('click', (event) => {
        if (!this.showingResults()) {
          this.manageContextMenu(event);
        }
      });
      this.view.on('hold', (event) => {
        if (!this.utilsService.isMobile() || this.showingResults()) {
          return;
        }
        this.manageContextMenu(event,true);
      });
    }).then(() => {
      if (callback) {
        callback();
      }
    });
    return this.view;
  }

  private manageContextMenu(event: any, mobile = false) {
    if(!this.contextMenu) return;
    this.contextMenu.nativeElement.style.display = 'none'
    this.contextMenu.nativeElement.latitude = null;
    this.contextMenu.nativeElement.longitude = null;
    if (event.button === 2 || mobile) {
      this.contextMenu.nativeElement.style.left = `${event.x}px`;
      if (event.x > this.view.container.clientWidth - 200) {
        this.contextMenu.nativeElement.style.left = `${event.x - 200}px`;
      }
      this.contextMenu.nativeElement.style.top = `${event.y}px`;
      if (event.y > this.view.container.clientHeight - 80) {
        this.contextMenu.nativeElement.style.top = `${event.y - 80}px`;
      }
      this.contextMenu.nativeElement.latitude = event.mapPoint.latitude;
      this.contextMenu.nativeElement.longitude = event.mapPoint.longitude;
      this.contextMenu.nativeElement.style.display = 'flex'
    }
  }


  private listenRotation() {
    this.view.watch('rotation', (newRotation) => {
      this.mapRotation.set(newRotation);
    })
  }

  public setMap(map: WebMap) {
    this.map = map;
  }

  public setView(view: MapView) {
    this.view = view;
  }

  public setCompassToInitial() {
    if (this.view && this.view.type === "2d") {
      this.view.rotation = 0;
    }
  }

  public addBaseWidgets(locateButton?: ElementRef) {
    this.manageZoomWidget();
    this.addHomeWidget();
    if (locateButton) {
      this.addLocateButton(locateButton);
    }
    this.addTrackWidget();
  }

  private addLocateButton(locateButton: ElementRef) {
    this.view.ui.add(locateButton.nativeElement, {
      position: "bottom-right"
    });
  }

  private addHomeWidget() {
    const homeWidget = new Home({
      view: this.view
    });
    this.view.ui.add(homeWidget, "bottom-right");
  }

  private addTrackWidget() {
    this.trackWidget = new Track({
      view: this.view,
      goToLocationEnabled: false,
    });
    this.view.ui.add(this.trackWidget, "top-left");
  }

  private manageZoomWidget() {
    this.view.ui.remove('zoom');

    const zoom = new Zoom({
      view: this.view
    });

    this.view.ui.add(zoom, {
      position: "bottom-right"
    });

  }

  public waitForClickToGetCoordinates() {
    return new Promise((resolve) => {
      const clickEvent = this.view.on('click', (event) => {
        clickEvent.remove();
        resolve(event.mapPoint);
      });
    });
  }

  public hideAllLayers() {
    this.map.layers.forEach((layer) => { layer.visible = false })
  }

  public showAllLayers() {
    this.map.layers.forEach((layer) => { layer.visible = true })
  }

  public drawItinerary(itinerary: Itinerary) {
    const pathsAllLegs: number[][][] = [];
    itinerary.legs.forEach((leg, index) => {
      const linestring = decode(leg.legGeometry.points);
      const orderedLinestring = linestring.map((point: any) => { return [point[1], point[0]] });
      const style = this.getLegStyle(leg)
      this.drawLinestring(orderedLinestring, style);

      if (index === 0) {
        this.createStartMarker(orderedLinestring[0]);
      } else {
        this.createLegMarker(leg, orderedLinestring[0]);
      }
      if (index === itinerary.legs.length - 1) {
        this.createEndMarker(orderedLinestring[orderedLinestring.length - 1]);
      }
      pathsAllLegs.push(...orderedLinestring);
    })
    const completeLinestring = new PolyLine({ paths: pathsAllLegs });
    this.view.goTo(completeLinestring.extent)
  }

  private getLegStyle(leg: Leg) {

    if (leg.mode === 'WALK' || leg.mode === 'BICYCLE') {
      return new SimpleLineSymbol({
        color: [0, 0, 0],
        width: 4,
        style: leg.mode === "WALK" ? "dot" : "dash",
      });
    }
    return new SimpleLineSymbol({
      color: `#${leg.routeColor}`, // RGB color
      width: 4
    });
  }

  private drawLinestring(linestring: any, style: SimpleLineSymbol | SimpleMarkerSymbol,) {
    const lineString = new PolyLine({ paths: linestring });
    const lineGraphic = new Graphic({
      geometry: lineString,
      symbol: style
    })
    this.view.graphics.add(lineGraphic);
    this.routeGraphicsDrawn.push(lineGraphic);
  }

  private createLegMarker(leg: Leg, position: number[]) {
    const legMarker = this.createRouteMarker(`assets/img/${leg.mode.toLowerCase()}_map.svg`, position, '40px', '40px')
    this.view.graphics.add(legMarker);
    this.routeGraphicsDrawn.push(legMarker);
  }

  private createStartMarker(position: number[]) {
    const startMarker = this.createRouteMarker('assets/img/a_from.svg', position)
    this.view.graphics.add(startMarker);
    this.routeGraphicsDrawn.push(startMarker);
  }

  private createEndMarker(position: number[]) {
    const endMarker = this.createRouteMarker('assets/img/b_to.svg', position)
    this.view.graphics.add(endMarker);
    this.routeGraphicsDrawn.push(endMarker);
  }

  private createRouteMarker(src: string, position: number[], width = '30px', height = '30px') {
    const pictureMarker = new PictureMarkerSymbol({
      url: src,
      width,
      height,
    })

    return new Graphic({
      geometry: new Point({
        spatialReference: this.view.spatialReference,
        longitude: position[0],
        latitude: position[1]
      }),
      symbol: pictureMarker
    });
  }

  public clearRouteGraphics() {
    this.routeGraphicsDrawn.forEach((graphic) => {
      this.view.graphics.remove(graphic);
    })
    this.routeGraphicsDrawn = [];
  }
}
